import { MainCard, MainCardTitle } from "src/components/main-card-type.tsx";
import { normalSanatizerBRL } from "src/utils/functions";
import type { ResultsType } from "../calculadora-custos-fixos_d";
import { CustosFixosGraph } from "./CustosFixosGraph";

export const CustosFixosResponse = ({ results }: { results: ResultsType }) => {
  // Função para renderizar insights personalizados
  const renderInsight = () => {
    const {
      cenario,
      percentualComprometido,
      percentualDisponivel,
      valorDisponivel,
      gap,
      custosEstiloVida,
      percentualEssencial,
    } = results;

    switch (cenario) {
      case "critico":
        return (
          <div className="p-6 bg-white rounded-lg border-2 border-red-200">
            <h3 className="text-xl font-bold text-gray-800 mb-3">
              Atenção! Seus custos comprometem {percentualComprometido}% da
              renda
            </h3>
            <p className="text-gray-700 mb-3 leading-relaxed">
              Com apenas <strong>{percentualDisponivel}%</strong> (R${" "}
              {normalSanatizerBRL(valorDisponivel)}) disponível, você tem pouca
              margem para investimentos e imprevistos. Para atingir a liberdade
              financeira, o ideal é destinar 25% da renda para investimentos.
            </p>

            {custosEstiloVida > 0 && (
              <p className="text-gray-700 mb-4 leading-relaxed">
                <strong>Identifique o que pode ser reduzido:</strong> Seus
                gastos com estilo de vida representam R${" "}
                {normalSanatizerBRL(custosEstiloVida)} (
                {results.percentualEstiloVida}% da renda). Revisar esses itens
                pode liberar recursos importantes.
              </p>
            )}

            <div className="mt-6 pt-4 border-t border-gray-200 text-center">
              <a
                href="https://www.auvp.com.br/?utm_source=site-is&utm_medium=calculadora-custos-fixos"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-primary text-white px-8 py-3 rounded font-semibold hover:opacity-90 transition-opacity"
              >
                Aprenda a organizar suas finanças →
              </a>
            </div>
          </div>
        );

      case "atencao":
        return (
          <div className="p-6 bg-white rounded-lg border-2 border-orange-200">
            <h3 className="text-xl font-bold text-gray-800 mb-3">
              Margem apertada - {percentualComprometido}% comprometido
            </h3>
            <p className="text-gray-700 mb-3 leading-relaxed">
              Seus custos fixos comprometem{" "}
              <strong>{percentualComprometido}%</strong> da sua renda, deixando
              apenas <strong>{percentualDisponivel}%</strong> (R${" "}
              {normalSanatizerBRL(valorDisponivel)}) disponível.
            </p>

            <p className="text-gray-700 mb-4 leading-relaxed">
              Para investir os 25% ideais e ainda ter margem para imprevistos,
              seria necessário
              {gap < 0
                ? ` liberar R$ ${normalSanatizerBRL(Math.abs(gap))} por mês`
                : " ajustar seus gastos"}
              .
            </p>

            {custosEstiloVida > 0 && (
              <p className="text-gray-700 mb-4 leading-relaxed">
                <strong>Você está próximo do equilíbrio!</strong> Pequenos
                ajustes podem liberar recursos para investimentos.
              </p>
            )}

            <div className="mt-6 pt-4 border-t border-gray-200 text-center">
              <a
                href="https://www.auvp.com.br"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-primary text-white px-8 py-3 rounded font-semibold hover:opacity-90 transition-opacity"
              >
                Descubra como otimizar seus gastos →
              </a>
            </div>
          </div>
        );

      case "equilibrado":
        if (gap >= 0) {
          return (
            <div className="p-6 bg-white rounded-lg border-2 border-blue-200">
              <h3 className="text-xl font-bold text-gray-800 mb-3">
                Seus custos estão equilibrados!
              </h3>
              <p className="text-gray-700 mb-3 leading-relaxed">
                Você tem <strong>{percentualDisponivel}%</strong> da renda
                disponível (R$ {normalSanatizerBRL(valorDisponivel)}/mês).
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                <strong>Ótima notícia!</strong> Você já tem margem suficiente
                para investir os 25% ideais (R${" "}
                {normalSanatizerBRL(results.valorIdealInvestimento)}) e ainda
                sobra R$ {normalSanatizerBRL(gap)} para lazer e imprevistos.
              </p>

              <div className="mt-6 pt-4 border-t border-gray-200 text-center">
                <a
                  href="https://www.auvp.com.br"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-primary text-white px-8 py-3 rounded font-semibold hover:opacity-90 transition-opacity"
                >
                  Aprenda a estratégia Buy & Hold →
                </a>
              </div>
            </div>
          );
        } else {
          return (
            <div className="p-6 bg-white rounded-lg border-2 border-blue-200">
              <h3 className="text-xl font-bold text-gray-800 mb-3">
                Boa margem para crescer!
              </h3>
              <p className="text-gray-700 mb-3 leading-relaxed">
                Você tem <strong>{percentualDisponivel}%</strong> da renda
                disponível (R$ {normalSanatizerBRL(valorDisponivel)}/mês).
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Para atingir os 25% ideais em investimentos, você ainda
                precisaria liberar R$ {normalSanatizerBRL(Math.abs(gap))} por
                mês. Pequenos ajustes podem fazer diferença!
              </p>

              <div className="mt-6 pt-4 border-t border-gray-200 text-center">
                <a
                  href="https://www.auvp.com.br/?utm_source=site-is&utm_medium=calculadora-custos-fixos"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-primary text-white px-8 py-3 rounded font-semibold hover:opacity-90 transition-opacity"
                >
                  Aprenda a otimizar e investir mais →
                </a>
              </div>
            </div>
          );
        }

      case "excelente":
        return (
          <div className="p-6 bg-white rounded-lg border-2 border-green-200">
            <h3 className="text-xl font-bold text-gray-800 mb-3">
              Parabéns! Alta capacidade de investimento
            </h3>
            <p className="text-gray-700 mb-3 leading-relaxed">
              Você tem <strong>{percentualDisponivel}%</strong> da renda
              disponível (R$ {normalSanatizerBRL(valorDisponivel)}/mês) para
              investir e melhorar sua qualidade de vida.
            </p>

            <p className="text-gray-700 mb-4 leading-relaxed">
              Com essa margem, você pode destinar os 25% ideais para
              investimentos (R${" "}
              {normalSanatizerBRL(results.valorIdealInvestimento)}) e ainda ter
              <strong> {(percentualDisponivel - 25).toFixed(1)}%</strong> livre
              (R$ {normalSanatizerBRL(gap)}).
            </p>

            {percentualEssencial < 35 && (
              <p className="text-gray-700 mb-4 leading-relaxed bg-gray-50 p-3 rounded border border-gray-200">
                <strong>Insight:</strong> Seus gastos essenciais são muito
                eficientes ({percentualEssencial}% da renda). Você tem espaço
                para equilibrar entre investir mais e melhorar sua qualidade de
                vida, se desejar.
              </p>
            )}

            {custosEstiloVida === 0 && (
              <p className="text-gray-700 mb-4 leading-relaxed bg-gray-50 p-3 rounded border border-gray-200">
                <strong>Avalie:</strong> Você não tem gastos com estilo de vida
                registrados. Lembre-se que o equilíbrio entre investir e viver
                bem é fundamental para a jornada sustentável rumo à liberdade
                financeira.
              </p>
            )}

            <div className="mt-6 pt-4 border-t border-gray-200 text-center">
              <a
                href="https://www.auvp.com.br/?utm_source=site-is&utm_medium=calculadora-custos-fixos"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-primary text-white px-8 py-3 rounded font-semibold hover:opacity-90 transition-opacity"
              >
                Acelere seus investimentos agora →
              </a>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Função para renderizar próximos passos
  const renderProximosPassos = () => {
    const { cenario, valorIdealInvestimento, custosEstiloVida } = results;

    switch (cenario) {
      case "critico":
        return (
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              Seus Próximos Passos
            </h3>
            <ol className="space-y-3">
              <li className="flex items-start">
                <span className="font-bold text-primary mr-2">1.</span>
                <div>
                  <strong className="text-gray-800">
                    Revise seus gastos de estilo de vida
                  </strong>
                  {custosEstiloVida > 0 && (
                    <p className="text-sm text-gray-600 mt-1">
                      Identifique R$ {normalSanatizerBRL(custosEstiloVida)} que
                      pode ser otimizado
                    </p>
                  )}
                </div>
              </li>
              <li className="flex items-start">
                <span className="font-bold text-primary mr-2">2.</span>
                <div>
                  <strong className="text-gray-800">
                    Negocie contratos fixos
                  </strong>
                  <p className="text-sm text-gray-600 mt-1">
                    Plano de celular, internet, seguros
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="font-bold text-primary mr-2">3.</span>
                <div>
                  <strong className="text-gray-800">
                    Aprenda a organizar seu orçamento
                  </strong>
                  <p className="text-sm text-gray-600 mt-1">
                    <a
                      href="https://www.auvp.com.br"
                      className="text-primary hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Conheça as estratégias da AUVP
                    </a>
                  </p>
                </div>
              </li>
            </ol>
          </div>
        );

      case "atencao":
        return (
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              Seus Próximos Passos
            </h3>
            <ol className="space-y-3">
              <li className="flex items-start">
                <span className="font-bold text-primary mr-2">1.</span>
                <div>
                  <strong className="text-gray-800">
                    Identifique pequenos ajustes
                  </strong>
                  <p className="text-sm text-gray-600 mt-1">
                    Pequenas reduções podem liberar grande valor
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="font-bold text-primary mr-2">2.</span>
                <div>
                  <strong className="text-gray-800">
                    Comece a investir gradualmente
                  </strong>
                  <p className="text-sm text-gray-600 mt-1">
                    Mesmo com pouco, é importante começar
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="font-bold text-primary mr-2">3.</span>
                <div>
                  <strong className="text-gray-800">
                    Aprenda estratégias de investimento
                  </strong>
                  <p className="text-sm text-gray-600 mt-1">
                    <a
                      href="https://www.auvp.com.br/?utm_source=site-is&utm_medium=calculadora-custos-fixos"
                      className="text-primary hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Conheça o método AUVP
                    </a>
                  </p>
                </div>
              </li>
            </ol>
          </div>
        );

      case "equilibrado":
      case "excelente":
        return (
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              Seus Próximos Passos
            </h3>
            <ol className="space-y-3">
              <li className="flex items-start">
                <span className="font-bold text-primary mr-2">1.</span>
                <div>
                  <strong className="text-gray-800">
                    Acelere seus investimentos
                  </strong>
                  <p className="text-sm text-gray-600 mt-1">
                    Destine R$ {normalSanatizerBRL(valorIdealInvestimento)}{" "}
                    mensalmente (25% da renda)
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="font-bold text-primary mr-2">2.</span>
                <div>
                  <strong className="text-gray-800">
                    Domine a estratégia profissional
                  </strong>
                  <p className="text-sm text-gray-600 mt-1">
                    <a
                      href="https://www.auvp.com.br/?utm_source=site-is&utm_medium=calculadora-custos-fixos"
                      className="text-primary hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Aprenda Buy & Hold com a AUVP
                    </a>
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="font-bold text-primary mr-2">3.</span>
                <div>
                  <strong className="text-gray-800">
                    Proteja seu patrimônio
                  </strong>
                  <p className="text-sm text-gray-600 mt-1">
                    Mantenha reserva de emergência e diversificação
                  </p>
                </div>
              </li>
            </ol>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <MainCard className="mt-4">
      <MainCardTitle>Análise dos seus Custos Fixos</MainCardTitle>

      {/* CARD HERO - Resumo Principal */}
      <div className="p-6 bg-white rounded-lg shadow-md border border-gray-200 mb-6">
        <div className="grid sm:grid-cols-2 gap-6 mb-6">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">Renda mensal</p>
            <p className="text-3xl font-bold text-gray-800">
              R$ {normalSanatizerBRL(results.rendaLiquida)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">Custos totais</p>
            <p className="text-3xl font-bold text-gray-800">
              R$ {normalSanatizerBRL(results.custosTotal)}
            </p>
          </div>
        </div>

        <div className="py-6 border-y border-gray-200 my-6 text-center">
          <p className="text-sm text-gray-600 mb-3">Comprometimento da Renda</p>
          <p className="text-4xl font-black text-primary">
            {results.percentualComprometido}%
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-sm text-gray-600 mb-1">Disponível</p>
            <p className="text-xl font-bold text-gray-800">
              R$ {normalSanatizerBRL(results.valorDisponivel)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {results.percentualDisponivel}%
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Ideal investir</p>
            <p className="text-xl font-bold text-primary">
              R$ {normalSanatizerBRL(results.valorIdealInvestimento)}
            </p>
            <p className="text-xs text-gray-500 mt-1">25%</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">
              {results.gap >= 0 ? "Sobra" : "Falta"}
            </p>
            <p
              className={`text-xl font-bold ${results.gap >= 0 ? "text-green-600" : "text-red-600"}`}
            >
              R$ {normalSanatizerBRL(Math.abs(results.gap))}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {results.gap >= 0 ? "após 25%" : "para 25%"}
            </p>
          </div>
        </div>
      </div>

      {/* MEDIDOR VISUAL DE SAÚDE FINANCEIRA */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h3 className="font-semibold text-center mb-3 text-gray-700">
          Medidor de Saúde Financeira
        </h3>
        <div className="relative">
          <div className="h-6 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-6 rounded-full ${
                results.cenario === "critico"
                  ? "bg-red-600"
                  : results.cenario === "atencao"
                    ? "bg-orange-500"
                    : results.cenario === "equilibrado"
                      ? "bg-blue-500"
                      : "bg-green-500"
              }`}
              style={{
                display: "block",
                width: `${results.percentualComprometido}%`,
                maxWidth: "100%",
                minWidth: "0%",
                height: "100%",
              }}
            />
          </div>
          <div className="flex justify-between text-xs mt-2 text-gray-600">
            <span>0%</span>
            <span className="text-green-600 font-semibold">
              50%
              <br />
              Ideal
            </span>
            <span className="text-blue-600 font-semibold">
              70%
              <br />
              Bom
            </span>
            <span className="text-red-600 font-semibold">
              80%
              <br />
              Alerta
            </span>
            <span>100%</span>
          </div>
        </div>
        <p className="text-center mt-3 text-sm font-medium text-gray-700">
          Você está na faixa:{" "}
          <span
            className={`font-bold ${
              results.cenario === "critico"
                ? "text-red-600"
                : results.cenario === "atencao"
                  ? "text-orange-600"
                  : results.cenario === "equilibrado"
                    ? "text-blue-600"
                    : "text-green-600"
            }`}
          >
            {results.cenario === "critico"
              ? "CRÍTICO"
              : results.cenario === "atencao"
                ? "ATENÇÃO"
                : results.cenario === "equilibrado"
                  ? "EQUILIBRADO"
                  : "EXCELENTE"}
          </span>
        </p>
      </div>

      {/* INSIGHTS PERSONALIZADOS */}
      <div className="mb-6">{renderInsight()}</div>

      {/* BREAKDOWN ESSENCIAIS VS ESTILO DE VIDA */}
      {results.custosEstiloVida > 0 && (
        <div className="mb-6 grid sm:grid-cols-2 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-800">Gastos Essenciais</h4>
              <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                Não negociáveis
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-800">
              R$ {normalSanatizerBRL(results.custosEssenciais)}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {results.percentualEssencial}% da renda
            </p>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-800">Estilo de Vida</h4>
              <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                Negociáveis
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-800">
              R$ {normalSanatizerBRL(results.custosEstiloVida)}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {results.percentualEstiloVida}% da renda
            </p>
          </div>
        </div>
      )}

      {/* GRÁFICO DE DISTRIBUIÇÃO */}
      {results.categorias.length > 0 && (
        <>
          <h2 className="font-bold text-primary text-xl mb-4 text-center">
            Distribuição dos Custos
          </h2>
          <CustosFixosGraph results={results} />
        </>
      )}

      {/* DETALHAMENTO POR CATEGORIA */}
      <h2 className="font-bold text-primary text-xl my-6 text-center">
        Detalhamento por Categoria
      </h2>

      <div className="space-y-4">
        {results.categorias.map((categoria, index) => (
          <div
            key={index}
            className="border border-gray-200 rounded-lg overflow-hidden bg-white"
          >
            <div className="flex justify-between items-center p-4 bg-gray-50 border-b border-gray-200">
              <div>
                <h3 className="font-semibold text-gray-800 text-lg">
                  {categoria.nome}
                </h3>
                <p className="text-xs text-gray-600">
                  {categoria.essencial ? "Essencial" : "Estilo de Vida"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-gray-800">
                  R$ {normalSanatizerBRL(categoria.total)}
                </p>
                <p className="text-sm text-gray-600">
                  {categoria.percentual}% do total
                </p>
              </div>
            </div>

            <div className="p-4">
              <ul className="space-y-2">
                {categoria.itens.map((item, itemIndex) => (
                  <li
                    key={itemIndex}
                    className="flex justify-between items-center text-sm border-b border-gray-100 pb-2 last:border-0"
                  >
                    <span className="text-gray-700">{item.descricao}</span>
                    <div className="text-right">
                      <span className="font-medium text-gray-800">
                        R$ {normalSanatizerBRL(item.valorMensal)}
                      </span>
                      {item.valor !== item.valorMensal && (
                        <span className="text-xs text-gray-500 ml-2">
                          (total: R$ {normalSanatizerBRL(item.valor)})
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* PRÓXIMOS PASSOS */}
      <div className="mt-8">{renderProximosPassos()}</div>

      {/* DISCLAIMER */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-xs text-gray-700 leading-relaxed">
          <strong>Importante:</strong> Esta análise considera apenas custos
          fixos mensais. Não estão incluídos: gastos variáveis (lazer, viagens),
          investimentos já realizados, dívidas ou financiamentos de longo prazo.
          Para uma análise financeira completa e personalizada, considere
          consultar um planejador financeiro ou conhecer os cursos da{" "}
          <a
            href="https://www.auvp.com.br/?utm_source=site-is&utm_medium=calculadora-custos-fixos"
            className="text-primary hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            AUVP Escola
          </a>
          .
        </p>
      </div>
    </MainCard>
  );
};
